import { useState, useMemo } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import SEOMeta from '@/components/feature/SEOMeta';
import MovieCard from '@/components/feature/MovieCard';
import { movies, categories } from '@/mocks/movies';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();
  const searchQuery = searchParams.get('search') || '';
  const [activeCategory, setActiveCategory] = useState(slug || 'all');

  const filteredMovies = useMemo(() => {
    let result = movies;

    if (activeCategory && activeCategory !== 'all') {
      result = result.filter((m) => m.categorySlug === activeCategory);
    }

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        (m) =>
          m.title.toLowerCase().includes(query) ||
          m.genre.some((g) => g.toLowerCase().includes(query)) ||
          m.director.toLowerCase().includes(query) ||
          m.cast.some((c) => c.toLowerCase().includes(query))
      );
    }

    return result;
  }, [activeCategory, searchQuery]);

  const currentCategory = categories.find((c) => c.slug === activeCategory);
  const pageTitle = searchQuery
    ? `搜索"${searchQuery}"的结果`
    : currentCategory && currentCategory.slug !== 'all'
    ? `${currentCategory.name} - 奇优影院免费在线观看`
    : '全部影片 - 奇优影院免费在线观看';
  const pageDesc = searchQuery
    ? `在奇优影院搜索"${searchQuery}"的影片结果，免费在线观看高清电影电视剧。`
    : currentCategory && currentCategory.slug !== 'all'
    ? `奇优影院${currentCategory.name}频道，提供最新最全的${currentCategory.name}资源，全部免费高清在线观看，无需付费。`
    : '奇优影院全部影片频道，涵盖动作片、喜剧片、科幻片、爱情片、恐怖片、国产剧、韩剧、美剧等各类影视资源。';

  return (
    <>
      <ScrollToTop />
      <SEOMeta
        title={pageTitle}
        description={pageDesc}
        keywords={`奇优影院,${currentCategory?.name || '全部影片'},免费在线观看,高清电影,电视剧全集`}
        canonicalUrl={`https://www.qiyoucinema.com/category/${activeCategory}`}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-16">
          <div className="bg-background-100 border-b border-background-200/70">
            <div className="max-w-[1200px] mx-auto">
              <Breadcrumb items={[
                { label: '首页', path: '/' },
                { label: currentCategory?.name || '分类浏览' },
              ]} />
            </div>
          </div>

          <div className="px-4 md:px-6 py-6">
            <div className="max-w-[1200px] mx-auto">
              <div className="mb-6">
                <h1 className="text-xl md:text-2xl font-heading font-bold text-foreground-800">
                  {searchQuery ? `搜索："${searchQuery}"` : currentCategory?.name || '全部影片'}
                </h1>
                {searchQuery && (
                  <p className="mt-2 text-sm text-foreground-600">
                    找到 {filteredMovies.length} 个相关结果
                  </p>
                )}
              </div>

              <div className="flex flex-wrap gap-2 mb-6">
                {categories.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    onClick={() => setActiveCategory(cat.slug)}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                      activeCategory === cat.slug
                        ? 'bg-primary-500 text-background-50'
                        : 'bg-background-100 text-foreground-600 border border-background-200/70 hover:border-primary-500/30 hover:text-primary-500'
                    }`}
                  >
                    <i className={`${cat.icon} text-xs`}></i>
                    {cat.name}
                  </Link>
                ))}
              </div>

              {filteredMovies.length > 0 ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3 md:gap-4" data-product-shop>
                  {filteredMovies.map((movie) => (
                    <MovieCard key={movie.id} movie={movie} />
                  ))}
                </div>
              ) : (
                <div className="text-center py-20">
                  <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-500">
                    <i className="ri-search-line text-2xl"></i>
                  </div>
                  <p className="mt-4 text-sm text-foreground-600">
                    {searchQuery ? `未找到与"${searchQuery}"相关的影片` : '暂无影片'}
                  </p>
                  <Link
                    to="/category/all"
                    className="inline-block mt-4 text-sm text-primary-500 hover:text-primary-600 transition-colors"
                  >
                    浏览全部影片
                  </Link>
                </div>
              )}
            </div>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}